import { RequestError } from '../utils/RequestError';
import { urlMap } from '../utils/fetchService';
import { httpRequest, unwrapHttpRequest } from '../utils/network.service';
import { AiProviderKey, AiReport, AiReportSummary, CreateAiProviderKeyPayload } from '../types/ai';

const request = async <T>(path: string, accessToken: string, method: 'GET' | 'POST' | 'DELETE' = 'GET', body?: unknown): Promise<T> =>
  unwrapHttpRequest<T>(
    await httpRequest({
      path: `${urlMap(path)}${path}`,
      method,
      accessToken,
      ...(body !== undefined ? { body } : {}),
    }),
  );

const asList = <T>(response: T[] | { data?: T[]; keys?: T[]; reports?: T[] }): T[] => {
  if (Array.isArray(response)) return response;
  return response?.data ?? response?.keys ?? response?.reports ?? [];
};

/**
 * The shared modal consumes axios-style errors. Keep the rest of this app on
 * RequestError while adapting only this callback boundary.
 */
export const asCreateKeyModalError = (error: unknown): Error & { response?: { status: number; data: unknown } } => {
  if (!(error instanceof RequestError)) return error as Error;

  const modalError = new Error(error.message) as Error & { response: { status: number; data: unknown } };
  const info = error.info as { message?: string; error?: string } | undefined;
  modalError.response = {
    status: error.status,
    data: { ...info, message: info?.message ?? info?.error ?? error.message },
  };
  return modalError;
};

export const listAiProviderKeys = async (accessToken: string): Promise<AiProviderKey[]> =>
  asList(await request<AiProviderKey[] | { data?: AiProviderKey[]; keys?: AiProviderKey[] }>('/organizations/ai-api-keys', accessToken));

export const createAiProviderKey = async (accessToken: string, payload: CreateAiProviderKeyPayload): Promise<AiProviderKey> => {
  try {
    return await request<AiProviderKey>('/organizations/ai-api-keys', accessToken, 'POST', payload);
  } catch (error) {
    throw asCreateKeyModalError(error);
  }
};

export const deleteAiProviderKey = async (accessToken: string, keyId: string): Promise<void> => {
  await request<void>(`/organizations/ai-api-keys/${encodeURIComponent(keyId)}`, accessToken, 'DELETE');
};

export const listEligibleAiReportKeys = async (accessToken: string, analysisId: string): Promise<AiProviderKey[]> =>
  asList(
    await request<AiProviderKey[] | { data?: AiProviderKey[]; keys?: AiProviderKey[] }>(
      `/eval/results/${encodeURIComponent(analysisId)}/ai-report-keys`,
      accessToken,
    ),
  );

export const createAiReport = (accessToken: string, analysisId: string, keyId: string): Promise<AiReportSummary> =>
  request<AiReportSummary>(`/eval/results/${encodeURIComponent(analysisId)}/ai-report`, accessToken, 'POST', { keyId });

export const listAiReports = async (accessToken: string, analysisId: string): Promise<AiReportSummary[]> =>
  asList(
    await request<AiReportSummary[] | { data?: AiReportSummary[]; reports?: AiReportSummary[] }>(
      `/eval/results/${encodeURIComponent(analysisId)}/ai-reports`,
      accessToken,
    ),
  );

export const getAiReport = (accessToken: string, analysisId: string, model: string): Promise<AiReport> =>
  request<AiReport>(`/eval/results/${encodeURIComponent(analysisId)}/ai-report?model=${encodeURIComponent(model)}`, accessToken);

export const pollAiReport = async (
  accessToken: string,
  analysisId: string,
  model: string,
  maxAttempts = 15,
  intervalMs = 2000,
): Promise<AiReportSummary> => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const reports = await listAiReports(accessToken, analysisId);
    const report = reports.find(candidate => candidate.model === model);
    if (report?.status === 'completed' || report?.status === 'failed') return report;
    if (attempt < maxAttempts - 1) {
      await new Promise(resolve => {
        setTimeout(resolve, intervalMs);
      });
    }
  }

  throw new Error('AI report is still processing. Check again later.');
};
